import React, { useState, useContext } from "react";
import { Col, Row, Table, Badge, Button, Spinner, Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import authenticatedFetch from "../../utils/fetch"
import NotyfContext from "../../contexts/NotyfContext";
import { UserSettingsContext } from "../../contexts/UserSettingsContext";
import { Trash2, AlertOctagon } from "react-feather";

const RevenueTeamManagement = () => {
  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const canEdit = isH3Admin && authorities.includes("H3_ADMIN_DATA_DELETION_EDIT");

  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [data, setData] = useState(null);


  const [migrateIsLoading, setMigrateIsLoading] = useState(false);

  const notyf = useContext(NotyfContext);

  const renderFieldGroups = (title, groups) => (
    <>
      <h5>{title}</h5>
      {(!groups || groups.length === 0) && <p className="text-muted">None</p>}
      {groups && groups.length > 0 &&
        <Table size="sm" bordered responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(group => (
              <tr key={group.id}>
                <td>{group.name}</td>
                <td>{group.fields.join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      }
    </>
  );

  const renderPreview = (result) => {
    if (!result) {
      return null;
    }

    return (
      <>
        {renderFieldGroups("Providers", result.providers)}
        {renderFieldGroups("Practices", result.practices)}
        {renderFieldGroups("Tasks", result.tasks)}

        <h5>S3 Documents</h5>
        <p className="mb-1">Object versions: {result.s3Documents.objectVersionCount}</p>
        <p className="mb-1">Delete markers: {result.s3Documents.deleteMarkerCount}</p>
        {result.s3Documents.sampleKeys && result.s3Documents.sampleKeys.length > 0 &&
          <>
          Sample keys:
          <ul>
            {result.s3Documents.sampleKeys.map(key => <li key={key}>{key}</li>)}
          </ul>
          </>
        }

        <h5>eSignature Envelopes</h5>
        <p className="mb-1">Count: {result.eSignatureEnvelopes.count}</p>
        {result.eSignatureEnvelopes.boldsignEnvelopeIds && result.eSignatureEnvelopes.boldsignEnvelopeIds.length > 0 &&
          <p className="mb-1">Envelope IDs: {result.eSignatureEnvelopes.boldsignEnvelopeIds.join(", ")}</p>
        }
        {result.eSignatureEnvelopes.failures && result.eSignatureEnvelopes.failures.length > 0 &&
          <>
            <p className="mb-1 text-danger">Failures:</p>
            <ul>
              {result.eSignatureEnvelopes.failures.map(failure => <li key={failure} className="text-danger">{failure}</li>)}
            </ul>
          </>
        }
      </>
    );
  };

  const handleGetDeletePreview =(e) => {
    e.preventDefault();
    setIsLoading(true);

    authenticatedFetch({
      path: "revenueteam/datadeletion/preview",
      successHandler: function(result) {
        setIsPreviewing(true);
        setData(result);
        setIsLoading(false);
      },
      errorHandler: function(error) {
        setIsPreviewing(false);
        setIsLoading(false);
        if (parseInt(error.message) === 417)
        {
          notyf.open({type: "error", message: "Revenue team must have end date in the past", duration: 5000, dismissable: true, ripple: true});
        }
        else
        {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
        
      }
    });
  };


  const handleDeleteData = (e) => {
    e.preventDefault();
    if (!canEdit) {
      alert("You do not have permission to delete data.");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete the revenue team data? This action cannot be undone.");
    if (!confirmed) {
      return;
    }

    setIsLoading(true);

    authenticatedFetch({
      path: "revenueteam/datadeletion/execute",
      method: "POST",
      successHandler: function(result) {
        setIsLoading(false);
        setIsPreviewing(false);
        setData(null);
        notyf.open({type: "success", message: "Delete initiated!  Check your inbox!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function(error) {
        setIsLoading(false);
        
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });

  };

  const handleMigrate = (e) => {
    e.preventDefault();
    if (!canEdit) {
      alert("You do not have permission to migrate data.");
      return;
    }

    setMigrateIsLoading(true);

    authenticatedFetch({
      path: "revenueteam/migratereminderssent",
      method: "POST",
      successHandler: function(result) {
        setMigrateIsLoading(false);
        setIsPreviewing(false);
        setData(null);
        notyf.open({type: "success", message: "Migration initiated!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function(error) {
        setMigrateIsLoading(false);
        
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });

  };


  return (
    <>
    <Row>
      <Helmet title="Revenue Team Management"/>
      <h1 className="h3 mb-3">Revenue Team Management
      
      </h1>
      <br /><br /><br />
      <Col lg="12">
      <AlertOctagon style={{'height': '14px', 'width': '14px'}}/>&nbsp;This action will delete all revenue team data. Please proceed with caution.
      <br /><br />
      {!isPreviewing && canEdit && 
      
      <>
      {isLoading ? <Spinner animation="border" size="sm" /> :
       <Button variant="danger" onClick={(e) => {handleGetDeletePreview(e)}} disabled={isLoading}><Trash2 style={{'height': '14px', 'width': '14px'}}/>&nbsp;Delete Data</Button>
      }
      </>

      }
      
      {isPreviewing && data &&
      <>
      <h4>
        <Badge bg={data.executed ? "danger" : "secondary"}>
          {data.executed ? "Executed" : "Preview"}
        </Badge>
      </h4>
      <div id="preview" style={{maxHeight: '500px', overflowY: 'auto', marginTop: '20px'}}>
        {renderPreview(data)}
      </div>
      <br /><br />
      {isLoading ? <Spinner animation="border" size="sm" /> :
        <Button variant="danger" onClick={(e) => {handleDeleteData(e)}}><Trash2 style={{'height': '14px', 'width': '14px'}}/>&nbsp;Confirm Delete</Button>
      }
      </>
      }
      </Col>
    </Row>

    {canEdit &&
    <Row style={{paddingTop: "30px"}}>
      <Col lg="12">
      {migrateIsLoading ? <Spinner animation="border" size="sm" /> :
       <Button variant="warning" onClick={(e) => {handleMigrate(e)}} disabled={migrateIsLoading}>Migrate Reminders Sent</Button>
      }
      </Col>
    </Row>
    }
    
    </>
  )
};

export default RevenueTeamManagement;
